import { create } from "zustand";
import type { Message, MessageStatus } from "@snowcord/shared";

interface ChatState {
  messages: Map<string, Message[]>;
  typingUsers: Map<string, string[]>;
  lastReadMessageId: Map<string, string | null>;

  addMessage: (roomId: string, message: Message) => void;
  addOptimistic: (roomId: string, tempId: string, message: Message) => void;
  confirmOptimistic: (roomId: string, tempId: string, realMessage: Message) => void;
  failOptimistic: (roomId: string, tempId: string) => void;
  editMessage: (roomId: string, messageId: string, content: string) => void;
  deleteMessage: (roomId: string, messageId: string) => void;
  addReaction: (roomId: string, messageId: string, emoji: string, userId: string) => void;

  setTyping: (roomId: string, userId: string, isTyping: boolean) => void;
  setLastRead: (roomId: string, messageId: string) => void;

  setMessages: (roomId: string, messages: Message[]) => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: new Map(),
  typingUsers: new Map(),
  lastReadMessageId: new Map(),

  addMessage: (roomId, message) =>
    set((state) => {
      const next = new Map(state.messages);
      const roomMessages = [...(next.get(roomId) || []), message];
      next.set(roomId, roomMessages);
      return { messages: next };
    }),

  addOptimistic: (roomId, tempId, message) =>
    set((state) => {
      const next = new Map(state.messages);
      const roomMessages = [
        ...(next.get(roomId) || []),
        { ...message, id: tempId, status: "pending" as MessageStatus },
      ];
      next.set(roomId, roomMessages);
      return { messages: next };
    }),

  confirmOptimistic: (roomId, tempId, realMessage) =>
    set((state) => {
      const next = new Map(state.messages);
      const roomMessages = (next.get(roomId) || []).map((msg) =>
        msg.id === tempId ? { ...realMessage, status: "confirmed" as MessageStatus } : msg,
      );
      next.set(roomId, roomMessages);
      return { messages: next };
    }),

  failOptimistic: (roomId, tempId) =>
    set((state) => {
      const next = new Map(state.messages);
      const roomMessages = (next.get(roomId) || []).map((msg) =>
        msg.id === tempId ? { ...msg, status: "failed" as MessageStatus } : msg,
      );
      next.set(roomId, roomMessages);
      return { messages: next };
    }),

  editMessage: (roomId, messageId, content) =>
    set((state) => {
      const next = new Map(state.messages);
      const roomMessages = (next.get(roomId) || []).map((msg) =>
        msg.id === messageId ? { ...msg, content, editedAt: new Date().toISOString() } : msg,
      );
      next.set(roomId, roomMessages);
      return { messages: next };
    }),

  deleteMessage: (roomId, messageId) =>
    set((state) => {
      const next = new Map(state.messages);
      const roomMessages = (next.get(roomId) || []).filter(
        (msg) => msg.id !== messageId,
      );
      next.set(roomId, roomMessages);
      return { messages: next };
    }),

  addReaction: (roomId, messageId, emoji, userId) =>
    set((state) => {
      const next = new Map(state.messages);
      const roomMessages = (next.get(roomId) || []).map((msg) => {
        if (msg.id !== messageId) return msg;
        const reactions = { ...msg.reactions };
        const users = reactions[emoji] || [];
        if (users.includes(userId)) {
          reactions[emoji] = users.filter((u) => u !== userId);
          if (reactions[emoji].length === 0) delete reactions[emoji];
        } else {
          reactions[emoji] = [...users, userId];
        }
        return { ...msg, reactions };
      });
      next.set(roomId, roomMessages);
      return { messages: next };
    }),

  setTyping: (roomId, userId, isTyping) =>
    set((state) => {
      const next = new Map(state.typingUsers);
      const users = next.get(roomId) || [];
      if (isTyping) {
        if (!users.includes(userId)) {
          next.set(roomId, [...users, userId]);
        }
      } else {
        next.set(
          roomId,
          users.filter((u) => u !== userId),
        );
      }
      return { typingUsers: next };
    }),

  setLastRead: (roomId, messageId) =>
    set((state) => {
      const next = new Map(state.lastReadMessageId);
      next.set(roomId, messageId);
      return { lastReadMessageId: next };
    }),

  setMessages: (roomId, messages) =>
    set((state) => {
      const next = new Map(state.messages);
      next.set(roomId, messages);
      return { messages: next };
    }),
}));
